// Standard CRA service worker registration helper, with onUpdate/onSuccess
// callbacks. Adapted from cra-template-pwa.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

let activeRegistration = null;
const UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

// Force a fresh check against the network for an updated SW. Cheap when there
// is no update — the browser sends a conditional GET. Callable any time after
// register() resolves (no-op until then).
export function triggerUpdate() {
  if (activeRegistration) {
    activeRegistration.update().catch(() => {});
  }
}

export function register(config) {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
      navigator.serviceWorker.ready.then(() => {
        console.log('Service worker ready (localhost dev)');
      });
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

// NOTE: no global controllerchange auto-reload. The Update toast
// (InstallPrompt) owns the reload — it posts SKIP_WAITING + listens
// for the matching controllerchange + reloads. Adding a second
// global listener here would yank the page out from under the user
// the moment a new SW activates, with no warning. The toast already
// handles its own reload race so this layer is redundant.

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      activeRegistration = registration;

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content is waiting — old SW is still controlling the page.
              if (config && config.onUpdate) config.onUpdate(registration);
            } else {
              // First-time install, content is now precached.
              if (config && config.onSuccess) config.onSuccess(registration);
            }
          }
        };
      };

      // iOS-friendly update check. registration.update() prompts the browser
      // to re-fetch the SW; on iOS PWAs onupdatefound often does NOT re-fire
      // when the SW was already in waiting state from a previous visit, so
      // we also probe registration.waiting after the update resolves and
      // dispatch onUpdate ourselves if a waiting worker exists.
      const checkForUpdates = () => {
        registration
          .update()
          .then(() => {
            if (registration.waiting && config && config.onUpdate) {
              config.onUpdate(registration);
            }
          })
          .catch(() => {});
      };

      // Run once immediately — covers the case where the SW is already
      // waiting from a prior session and onupdatefound never fires this time.
      if (registration.waiting && config && config.onUpdate) {
        config.onUpdate(registration);
      }

      // Force an update check whenever the user brings the app to the
      // foreground (key for installed PWAs, which otherwise rely on the
      // browser's own infrequent background check).
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdates();
      });

      // Periodic check while the app stays open (no-ops when backgrounded
      // because timers are suspended on iOS PWAs and most mobile browsers).
      setInterval(checkForUpdates, UPDATE_INTERVAL_MS);
    })
    .catch((error) => {
      console.error('Service worker registration failed:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet — app running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => {
        console.error(error.message);
      });
  }
}
